import { prepareTextPanelsList } from 'engage/routes/CompanySettings/components/TextPanels/utils';
import VideoService from 'engage/service/VideoService';
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';

const INIT_TEXT = '';
// Default context value
const DEFAULT_CONTEXT = {
  textPanelsTemplatesList: [],
  isTextPanelEditor: false,
  enableTextPanelEditor: false,
  disableTextPanelEditor: false,
  topText: INIT_TEXT,
  bottomText: INIT_TEXT,
  updateTopText: () => { },
  updateBottomText: () => { },
  removeTextPanel: () => { },
  initTextPanelsData: () => { },
  isTextPanelsChanged: false,
  resetEditorL: () => { },
  editorSceneId: null,
  setEditorSceneId: () => { },
  currentTemplateId: null,
  updateTemplateId: () => { },
  currentTemplate: {},
  isLoadingList: false,
};

// Create the context
const TextPanelContext = createContext(DEFAULT_CONTEXT);

const TextPanelProvider = ({ children }) => {
  const [isTextPanelEditor, setIsTextPanelEditor] = useState(false);
  const [topText, setTopText] = useState(INIT_TEXT);
  const [bottomText, setBottomText] = useState(INIT_TEXT);
  const [isTextPanelsChanged, setIsTextPanelsChanged] = useState(false);
  const [textPanelsTemplatesList, setTextPanelsTemplatesList] = useState([]);
  const [currentTemplateId, setCurrentTemplateId] = useState(null);
  const [isLoadingList, setIsLoadingList] = useState(false);

  const [editorSceneId, setEditorSceneId] = useState(null);

  const enableTextPanelEditor = useCallback(async sceneId => {
    setIsTextPanelEditor(true);
    setEditorSceneId(sceneId);
    if (!textPanelsTemplatesList.length) {
      setIsLoadingList(true);
      try {
        const { data = null } = await VideoService.getTextPanels();
        if (data) {
          const preparedData = await prepareTextPanelsList(data, true);
          setTextPanelsTemplatesList(preparedData);
          setCurrentTemplateId(prevState => prevState || (preparedData.find((item) => item.active)?.id ?? preparedData[0]?.id));
        }
      } catch (error) {
        console.log('Error while fetching text panels templates', error);
      } finally {
        setIsLoadingList(false);
      }
    }
  }, [textPanelsTemplatesList]);

  const resetEditor = useCallback(() => {
    setTopText(INIT_TEXT);
    setBottomText(INIT_TEXT);
    setIsTextPanelsChanged(false);
    setEditorSceneId(null);
  }, [textPanelsTemplatesList]);

  const disableTextPanelEditor = useCallback(() => {
    resetEditor();
    setIsTextPanelEditor(false);
  }, [setIsTextPanelEditor, setTopText, setBottomText]);

  const removeTextPanel = useCallback(() => {
    setTopText(INIT_TEXT);
    setBottomText(INIT_TEXT);
    !isTextPanelsChanged && setIsTextPanelsChanged(true);
  });

  const updateTopText = text => {
    setTopText(text ?? '');
    !isTextPanelsChanged && setIsTextPanelsChanged(true);
  };

  const updateBottomText = text => {
    setBottomText(text ?? '');
    !isTextPanelsChanged && setIsTextPanelsChanged(true);
  };

  const updateTemplateId = (id = null) => {
    setCurrentTemplateId(id);
    !isTextPanelsChanged && setIsTextPanelsChanged(true);
  }

  const currentTemplate = useMemo(() => textPanelsTemplatesList.find(({ id }) => id === currentTemplateId), [textPanelsTemplatesList, currentTemplateId]);

  const initTextPanelsData = (nameCard) => {
    if (nameCard) {
      const { topText, bottomText, templateId = null } = nameCard;
      setTopText(topText);
      setBottomText(bottomText);
      if (templateId) {
        setCurrentTemplateId(templateId);
        const templateDiv = document.getElementById(`${templateId}`);
        templateDiv && templateDiv.scrollIntoView({ behavior: 'smooth' });
      } 
    } else {
      setTopText(INIT_TEXT);
      setBottomText(INIT_TEXT);
    }
    setIsTextPanelsChanged(false);
  };

  const value = useMemo(
    () => ({
      isTextPanelEditor,
      enableTextPanelEditor,
      disableTextPanelEditor,
      topText,
      bottomText,
      updateTopText,
      updateBottomText,
      removeTextPanel,
      initTextPanelsData,
      isTextPanelsChanged,
      resetEditor,
      editorSceneId,
      setEditorSceneId,
      textPanelsTemplatesList,
      currentTemplateId,
      updateTemplateId,
      currentTemplate,
      isLoadingList,
    }),
    [
      isTextPanelEditor,
      topText,
      bottomText,
      isTextPanelsChanged,
      editorSceneId,
      textPanelsTemplatesList,
      currentTemplateId,
      enableTextPanelEditor,
      isLoadingList,
    ]
  );
  return (
    <TextPanelContext.Provider value={value}>
      {children}
    </TextPanelContext.Provider>
  );
}


const useTextPanelContext = () => {
  const context = useContext(TextPanelContext);
  if (!context || context === DEFAULT_CONTEXT) {
    return DEFAULT_CONTEXT; // Fallback to default value
  }
  return context;
}

export { TextPanelProvider, useTextPanelContext };
export default TextPanelContext;
